import {
  collectRequestTransferables,
  type ProcessingRequest,
  type ProcessingResult,
} from '../processing/contracts';
import {
  ProcessingCancelledError,
  ProcessingError,
  RemoteProcessingError,
} from '../processing/errors';
import {
  assertProgressUpdate,
  type ProcessingProgressEvent,
} from '../processing/progress';
import {
  isProcessingWorkerResponseMessage,
  type ProcessingWorkerRequestMessage,
  type ProcessingWorkerResponseMessage,
} from './protocol';

export interface ProcessingWorkerTransport {
  send(
    message: ProcessingWorkerRequestMessage,
    transferables?: readonly Transferable[],
  ): void;
  subscribe(
    onMessage: (message: unknown) => void,
    onError: () => void,
  ): () => void;
  terminate(): void;
}

export function createBrowserWorkerTransport(worker: Worker): ProcessingWorkerTransport {
  return {
    send(message, transferables = []) {
      worker.postMessage(message, [...transferables]);
    },
    subscribe(onMessage, onError) {
      const messageListener = (event: MessageEvent<unknown>) => onMessage(event.data);
      const errorListener = () => onError();
      worker.addEventListener('message', messageListener);
      worker.addEventListener('error', errorListener);
      worker.addEventListener('messageerror', errorListener);
      return () => {
        worker.removeEventListener('message', messageListener);
        worker.removeEventListener('error', errorListener);
        worker.removeEventListener('messageerror', errorListener);
      };
    },
    terminate() {
      worker.terminate();
    },
  };
}

export interface WorkerRunOptions {
  readonly signal?: AbortSignal;
  readonly hardCancellationTimeoutMs?: number;
  readonly onProgress?: (event: ProcessingProgressEvent) => void;
}

export class ProcessingWorkerClient {
  private started = false;
  private settleHardTermination: (() => void) | undefined;

  public constructor(private readonly transport: ProcessingWorkerTransport) {}

  public run(
    request: ProcessingRequest,
    options: WorkerRunOptions = {},
  ): Promise<ProcessingResult> {
    if (this.started) {
      return Promise.reject(
        new ProcessingError('processing.duplicateJob', { jobId: request.jobId }),
      );
    }
    this.started = true;
    if (options.signal?.aborted === true) {
      return Promise.reject(new ProcessingCancelledError(false));
    }

    const hardCancellationTimeoutMs = options.hardCancellationTimeoutMs ?? 750;
    if (!Number.isFinite(hardCancellationTimeoutMs) || hardCancellationTimeoutMs < 0) {
      return Promise.reject(
        new ProcessingError('processing.invalidProtocol', {
          reason: 'cancellationTimeout',
        }),
      );
    }

    return new Promise<ProcessingResult>((resolve, reject) => {
      let settled = false;
      let cancellationRequested = false;
      let cancellationTimer: ReturnType<typeof setTimeout> | undefined;
      let lastProgressSequence = 0;

      const unsubscribe = this.transport.subscribe(handleMessage, handleWorkerError);

      const cleanup = () => {
        unsubscribe();
        if (cancellationTimer !== undefined) clearTimeout(cancellationTimer);
        options.signal?.removeEventListener('abort', handleAbort);
        this.settleHardTermination = undefined;
      };

      const settleReject = (error: ProcessingError) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };

      const settleResolve = (result: ProcessingResult) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };

      const hardTerminate = () => {
        if (settled) return;
        this.transport.terminate();
        settleReject(new ProcessingCancelledError(true));
      };
      this.settleHardTermination = hardTerminate;

      const requestCancellation = () => {
        if (settled || cancellationRequested) return;
        cancellationRequested = true;
        try {
          this.transport.send({ type: 'processing.cancel', jobId: request.jobId });
        } catch {
          hardTerminate();
          return;
        }
        cancellationTimer = setTimeout(hardTerminate, hardCancellationTimeoutMs);
      };

      function handleAbort() {
        requestCancellation();
      }

      function handleWorkerError() {
        settleReject(new ProcessingError('processing.workerFailed', undefined, true));
      }

      function handleMessage(message: unknown) {
        if (!isProcessingWorkerResponseMessage(message)) {
          settleReject(
            new ProcessingError('processing.invalidProtocol', {
              reason: 'workerMessage',
            }),
          );
          return;
        }

        const response = message as ProcessingWorkerResponseMessage;
        const responseJobId =
          response.type === 'processing.progress' ? response.event.jobId : response.jobId;
        if (responseJobId !== request.jobId) {
          settleReject(
            new ProcessingError('processing.invalidProtocol', {
              reason: 'jobId',
            }),
          );
          return;
        }

        if (response.type === 'processing.progress') {
          try {
            assertProgressUpdate(response.event);
            if (response.event.sequence <= lastProgressSequence) {
              throw new ProcessingError('processing.invalidProgress', {
                reason: 'sequence',
              });
            }
            lastProgressSequence = response.event.sequence;
            options.onProgress?.(response.event);
          } catch (error) {
            settleReject(
              error instanceof ProcessingError
                ? error
                : new ProcessingError('processing.invalidProgress'),
            );
          }
          return;
        }

        if (response.type === 'processing.cancelled') {
          settleReject(new ProcessingCancelledError(false));
        } else if (response.type === 'processing.error') {
          settleReject(new RemoteProcessingError(response.failure));
        } else if (cancellationRequested) {
          settleReject(new ProcessingCancelledError(false));
        } else {
          settleResolve(response.result);
        }
      }

      options.signal?.addEventListener('abort', handleAbort, { once: true });
      try {
        this.transport.send(
          { type: 'processing.run', request },
          collectRequestTransferables(request),
        );
      } catch {
        settleReject(new ProcessingError('processing.workerFailed', undefined, true));
      }
    });
  }

  /** Immediately terminates an unresponsive or unsafe worker and rejects its job. */
  public hardTerminate(): void {
    this.settleHardTermination?.();
  }
}

